import React, { useState, useEffect } from 'react';
import client from '../api/client';

/**
 * Control panel for starting, pausing, resuming, stopping, and canceling LP tracking runs.
 * Displays run status and elapsed time.
 */
function LPRunControlPanel({ activeRun, onRunUpdated }) {
    const [loading, setLoading] = useState(false);
    const [elapsedTime, setElapsedTime] = useState(0);

    // Update elapsed time every second when run is active
    useEffect(() => {
        if (!activeRun || !activeRun.started_at) {
            return;
        }

        const updateElapsedTime = () => {
            const startTime = new Date(activeRun.started_at);
            const now = new Date();
            const totalElapsed = (now - startTime) / 1000; // in seconds
            const pausedDuration = activeRun.total_paused_duration || 0;

            if (activeRun.is_paused) {
                // If paused, use the paused_at time
                const pausedAt = new Date(activeRun.paused_at);
                const activeElapsed = (pausedAt - startTime) / 1000 - pausedDuration;
                setElapsedTime(Math.max(0, activeElapsed));
            } else {
                // If active, calculate current elapsed time
                const activeElapsed = totalElapsed - pausedDuration;
                setElapsedTime(Math.max(0, activeElapsed));
            }
        };

        updateElapsedTime();
        const interval = setInterval(updateElapsedTime, 1000);

        return () => clearInterval(interval);
    }, [activeRun]);

    const formatTime = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);
        return `${hours}h ${minutes}m ${secs}s`;
    };

    const handleStart = async () => {
        if (window.confirm('Start a new LP tracking run? This will take snapshots of your selected characters.')) {
            setLoading(true);
            try {
                await client.post('/lp/tracking/start/');
                alert('LP tracking run started successfully!');
                if (onRunUpdated) onRunUpdated();
            } catch (err) {
                console.error('Error starting run:', err);
                alert(err.response?.data?.error || 'Failed to start run');
            } finally {
                setLoading(false);
            }
        }
    };

    const handlePause = async () => {
        setLoading(true);
        try {
            await client.post('/lp/tracking/pause/');
            if (onRunUpdated) onRunUpdated();
        } catch (err) {
            console.error('Error pausing run:', err);
            alert(err.response?.data?.error || 'Failed to pause run');
        } finally {
            setLoading(false);
        }
    };

    const handleResume = async () => {
        setLoading(true);
        try {
            await client.post('/lp/tracking/resume/');
            if (onRunUpdated) onRunUpdated();
        } catch (err) {
            console.error('Error resuming run:', err);
            alert(err.response?.data?.error || 'Failed to resume run');
        } finally {
            setLoading(false);
        }
    };

    const handleStop = async () => {
        if (window.confirm('Stop and save this LP tracking run? This will take final snapshots of all characters.')) {
            setLoading(true);
            try {
                const response = await client.post('/lp/tracking/stop/');
                const metrics = response.data.metrics;

                alert(
                    `Run stopped successfully!\n\n` +
                    `LP Gained: ${metrics.lp_gained.toLocaleString()} LP\n` +
                    `Wallet Change: ${metrics.wallet_diff.toLocaleString()} ISK\n` +
                    `Total ISK: ${metrics.total_isk.toLocaleString()} ISK\n` +
                    `ISK/Hour: ${metrics.isk_per_hour.toLocaleString()} ISK/hr`
                );

                if (onRunUpdated) onRunUpdated();
            } catch (err) {
                console.error('Error stopping run:', err);
                alert(err.response?.data?.error || 'Failed to stop run');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleCancel = async () => {
        if (window.confirm('Cancel this LP tracking run? All data for this run will be lost!')) {
            setLoading(true);
            try {
                await client.post('/lp/tracking/cancel/');
                alert('Run cancelled successfully');
                if (onRunUpdated) onRunUpdated();
            } catch (err) {
                console.error('Error cancelling run:', err);
                alert(err.response?.data?.error || 'Failed to cancel run');
            } finally {
                setLoading(false);
            }
        }
    };

    if (!activeRun) {
        return (
            <section className="panel">
                <div className="panel-head">
                    <h2>Tracking run</h2>
                    <span className="panel-sub">Snapshots the selected characters' LP and wallets now, and again when you stop.</span>
                    <div className="panel-actions">
                        <button onClick={handleStart} disabled={loading} className="btn btn-primary">
                            {loading ? 'Starting...' : 'Start run'}
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="panel">
            <div className="panel-head">
                <h2>Tracking run</h2>
                <span className={`pill ${activeRun.is_paused ? 'pill-warn' : 'pill-pos'}`}>
                    {activeRun.is_paused ? 'Paused' : 'Running'}
                </span>
                <div className="panel-actions">
                    {!activeRun.is_paused ? (
                        <button onClick={handlePause} disabled={loading} className="btn">
                            {loading ? 'Pausing...' : 'Pause'}
                        </button>
                    ) : (
                        <button onClick={handleResume} disabled={loading} className="btn">
                            {loading ? 'Resuming...' : 'Resume'}
                        </button>
                    )}
                    <button onClick={handleStop} disabled={loading} className="btn btn-primary">
                        {loading ? 'Stopping...' : 'Stop and save'}
                    </button>
                    <button onClick={handleCancel} disabled={loading} className="btn btn-danger">
                        {loading ? 'Cancelling...' : 'Discard'}
                    </button>
                </div>
            </div>
            <div className="panel-body lp-run">
                <div className="lp-run-stat">
                    <span>Active time{activeRun.is_paused && ' (excludes paused time)'}</span>
                    <b>{formatTime(elapsedTime)}</b>
                </div>
                <div className="lp-run-stat">
                    <span>Started</span>
                    <b>{new Date(activeRun.started_at).toLocaleString()}</b>
                </div>
                <div className="lp-run-stat">
                    <span>Characters</span>
                    <div className="char-chips">
                        {activeRun.characters && activeRun.characters.map(char => (
                            <span key={char.character_id} className="char-chip selected">
                                {char.portrait_url && <img src={char.portrait_url} alt="" className="char-chip-portrait" />}
                                <b>{char.character_name}</b>
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

export default LPRunControlPanel;
